/* =============================================================================
   CART-DRAWER.JS — StylishRugs Theme
   Cart drawer Web Component — no dependencies, vanilla JS only
   ============================================================================= */

'use strict';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.items = this.querySelector('[data-cart-items]');
    this.subtotalEl = this.querySelector('[data-cart-subtotal]');
    this.countEl = this.querySelector('[data-cart-count]');
    this.shippingBar = this.querySelector('[data-shipping-bar]');
    this.shippingProgress = this.querySelector('[data-shipping-progress]');
    this.shippingText = this.querySelector('[data-shipping-text]');
    this.loader = this.querySelector('[data-cart-loader]');

    this.threshold = parseInt(this.dataset.freeShippingThreshold || '0', 10) * 100;

    // Open/close events
    PubSub.subscribe(EVENTS.CART_OPEN, () => this.open());
    PubSub.subscribe(EVENTS.CART_CLOSE, () => this.close());

    // Close button
    this.querySelector('[data-cart-close]')?.addEventListener('click', () => this.close());

    // Delegate item-level events
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-remove-item]')) {
        const btn = e.target.closest('[data-remove-item]');
        this.removeItem(parseInt(btn.dataset.line, 10));
      }
    });

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-quantity-input]')) {
        const input = e.target;
        this.updateQuantity(parseInt(input.dataset.line, 10), parseInt(input.value, 10));
      }
    });

    // Cart note
    const noteInput = this.querySelector('[data-cart-note]');
    if (noteInput) {
      noteInput.addEventListener(
        'change',
        debounce(() => CartAPI.updateNote(noteInput.value), 500)
      );
    }
  }

  open() {
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    PubSub.publish(EVENTS.OVERLAY_SHOW);
    this.setAttribute('aria-hidden', 'false');
    const closeBtn = this.querySelector('[data-cart-close]');
    if (closeBtn) trapFocus(this, closeBtn);
    this.refresh();
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    PubSub.publish(EVENTS.OVERLAY_HIDE);
    this.setAttribute('aria-hidden', 'true');
    removeTrapFocus();
  }

  async refresh() {
    this.setLoading(true);
    try {
      const cart = await CartAPI.getCart();
      this.renderCart(cart);
    } catch (e) {
      console.error('Cart refresh failed', e);
    } finally {
      this.setLoading(false);
    }
  }

  async removeItem(line) {
    this.setLoading(true);
    try {
      const cart = await CartAPI.removeItem(line);
      this.renderCart(cart);
      PubSub.publish(EVENTS.CART_UPDATE, cart);
    } catch (e) {
      console.error(e);
    } finally {
      this.setLoading(false);
    }
  }

  async updateQuantity(line, quantity) {
    this.setLoading(true);
    try {
      const cart = await CartAPI.updateItem(line, quantity);
      this.renderCart(cart);
      PubSub.publish(EVENTS.CART_UPDATE, cart);
    } catch (e) {
      console.error(e);
    } finally {
      this.setLoading(false);
    }
  }

  renderCart(cart) {
    // Update count
    if (this.countEl) this.countEl.textContent = `(${cart.item_count})`;

    // Update shipping bar
    if (this.shippingBar && this.threshold > 0) {
      const pct = Math.min((cart.total_price / this.threshold) * 100, 100);
      if (this.shippingProgress) this.shippingProgress.style.width = pct + '%';
      if (this.shippingText) {
        if (cart.total_price >= this.threshold) {
          this.shippingText.innerHTML = `<strong>You qualify for free shipping!</strong>`;
        } else {
          const remaining = formatMoney(this.threshold - cart.total_price);
          this.shippingText.innerHTML = `Add <strong>${remaining}</strong> more for free shipping`;
        }
      }
    }

    // Update subtotal
    if (this.subtotalEl) {
      this.subtotalEl.textContent = formatMoney(cart.total_price);
    }

    // Render items
    if (!this.items) return;

    if (cart.item_count === 0) {
      this.items.innerHTML = `
        <div class="cart-drawer__empty">
          <svg class="cart-drawer__empty-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
          <p class="cart-drawer__empty-text">Your cart is empty</p>
          <p class="cart-drawer__empty-subtext">Start shopping to add items</p>
          <a href="/collections/all" class="button button--secondary">Continue shopping</a>
        </div>
      `;
      return;
    }

    this.items.innerHTML = cart.items.map((item, index) => {
      const line = index + 1;
      const image = item.image
        ? `<img src="${item.image.replace(/(\.[a-z]+)$/, '_80x80$1')}" alt="${item.product_title}" width="80" height="80" loading="lazy">`
        : `<div style="background:#f0f0f0;width:100%;height:100%;"></div>`;

      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<span class="cart-drawer__item-variant">${item.variant_title}</span>`
        : '';

      return `
        <div class="cart-drawer__item" data-key="${item.key}">
          <a href="${item.url}" class="cart-drawer__item-image">
            ${image}
          </a>
          <div class="cart-drawer__item-details">
            <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
            ${variantTitle}
            <div class="cart-drawer__item-bottom">
              <quantity-input class="quantity">
                <button class="quantity__button" data-minus aria-label="Decrease quantity">&#8722;</button>
                <input
                  class="quantity__input"
                  type="number"
                  value="${item.quantity}"
                  min="0"
                  data-line="${line}"
                  data-quantity-input
                  aria-label="Quantity for ${item.product_title}"
                >
                <button class="quantity__button" data-plus aria-label="Increase quantity">&#43;</button>
              </quantity-input>
              <span class="cart-drawer__item-price">${formatMoney(item.line_price)}</span>
            </div>
            <button class="cart-drawer__item-remove" data-line="${line}" data-remove-item>Remove</button>
          </div>
        </div>
      `;
    }).join('');
  }

  setLoading(state) {
    if (this.loader) {
      this.loader.classList.toggle('is-loading', state);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ---------------------------------------------------------------------------
   Cart open triggers (header cart icon, etc.)
   --------------------------------------------------------------------------- */
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-open-cart]')) {
    e.preventDefault();
    PubSub.publish(EVENTS.CART_OPEN);
  }
});
